import React from "react";
import { formatFindingName } from "../utils/textFormatters";
import { hasDot, priorityLabel } from "../utils/findingPriority";

interface Props {
  condition: string;
  category: string | null;
  imageUrl?: string | null;
  /** True while the slice is still loading. Renders a calm shimmer skeleton.
   * False + no imageUrl = genuinely unavailable → quiet "no preview" label. */
  loading?: boolean;
}

export const FindingPreviewTile: React.FC<Props> = ({
  condition,
  category,
  imageUrl,
  loading = false,
}) => {
  // finding name = the finding segment, NOT the trailing modifier (fixes the old
  // .split("/").pop() bug that surfaced "Bilateral"). Path is <organ>/<finding>/<modifiers...>.
  const segments = condition.split("/");
  const name = formatFindingName(segments[1] ?? segments[0]);
  const organ = formatFindingName(segments[0]);
  return (
    <div className="flex flex-col">
      {/* Square image area — sized by the grid column width. aspect-square gives
          the tile a real height; the auto-fill grid sets no row height, so a
          flex-1 image area would otherwise collapse to nothing. */}
      <div className="relative aspect-square overflow-hidden rounded-sharp border border-border bg-black">
        {imageUrl ? (
          <img src={imageUrl} alt={`${name} preview`} className="h-full w-full object-contain" />
        ) : loading ? (
          // Blue spinner on the dark tile — reads as "working", not a broken
          // grey dim. Mirrors the viewer's canonical loader (VolumeViewport).
          <div className="flex h-full w-full items-center justify-center bg-bg-2">
            <div
              role="status"
              aria-label="Loading preview"
              className="h-6 w-6 animate-spin rounded-full border-2 border-signal border-t-transparent"
            />
          </div>
        ) : (
          // Genuinely unavailable (not canonicalized, no slice)
          <div className="flex h-full items-center justify-center">
            <span className="text-[10px] text-fg-faint">no preview</span>
          </div>
        )}
        {/* intentionally NO bbox / NO burned-in label — 510(k) clean preview */}
      </div>
      <div className="mt-2 flex items-start gap-1.5">
        {hasDot(category) && (
          <span
            data-testid="priority-dot"
            className="mt-[3px] h-1.5 w-1.5 flex-none rounded-full bg-signal"
          />
        )}
        <div className="min-w-0">
          <div className="font-mono text-[8px] uppercase leading-tight tracking-[0.12em] text-fg-dim">
            {organ}
            {priorityLabel(category) ? ` · ${priorityLabel(category)}` : ""}
          </div>
          <div className="truncate text-[13px] leading-snug text-fg">{name}</div>
        </div>
      </div>
    </div>
  );
};
